import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Combobox } from '../ui/combobox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Plus, Check, Loader2, AlertTriangle, Trash2, ShoppingCart, Calendar, Pill, Package, History
} from 'lucide-react';

const hojeISO = () => new Date().toISOString().split('T')[0];
const fmtData = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '');

const DispensacaoNaoPrescritoModal = ({
  open, onOpenChange, estoqueItens, onConfirm, saving, dataPadrao, pacienteNome,
}) => {
  const vazio = { item_id: '', quantidade: 1, justificativa: '' };
  const [form, setForm] = useState(vazio);
  const [data, setData] = useState(dataPadrao || hojeISO());
  const [cart, setCart] = useState([]);

  useEffect(() => { if (open) setData(dataPadrao || hojeISO()); }, [open, dataPadrao]);

  const itemSel = estoqueItens.find(i => i.id === form.item_id);
  const retroativa = data && data < hojeISO();
  const dataInvalida = !data || data > hojeISO();
  const qtdInvalida = !form.quantidade || Number(form.quantidade) <= 0 || (itemSel && Number(form.quantidade) > itemSel.estoque_atual);

  const handleAddToCart = () => {
    if (!form.item_id || qtdInvalida) return;
    setCart(prev => [...prev, {
      _id: Date.now() + Math.random(),
      item_id: form.item_id,
      item_nome: itemSel?.nome || '',
      item_unidade: itemSel?.unidade || 'un',
      item_tipo: itemSel?.tipo || 'material',
      quantidade: parseFloat(form.quantidade),
    }]);
    setForm(f => ({ ...f, item_id: '', quantidade: 1 }));
  };

  const handleRemoveFromCart = (id) => setCart(prev => prev.filter(i => i._id !== id));

  const reset = () => { setCart([]); setForm(vazio); };
  const handleConfirm = async () => { await onConfirm(cart, form.justificativa.trim(), data); reset(); };
  const handleOpenChange = (val) => { if (!val) reset(); onOpenChange(val); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="dialog-nao-prescrito">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Dispensar itens não prescritos
          </DialogTitle>
          <DialogDescription>
            {pacienteNome ? <span className="font-medium text-foreground">{pacienteNome} · </span> : null}
            Adicione os itens ao carrinho, informe a data e a justificativa e confirme de uma vez.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* Data */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end rounded-lg border bg-muted/30 p-3">
            <div className="sm:col-span-4 space-y-1">
              <Label className="text-sm flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Data da dispensação *</Label>
              <Input type="date" value={data} max={hojeISO()} onChange={(e) => setData(e.target.value)} data-testid="input-np-data" />
            </div>
            <div className="sm:col-span-8 text-xs" data-testid="np-data-aviso">
              {dataInvalida ? (
                <span className="text-red-600">Informe uma data válida (não futura).</span>
              ) : retroativa ? (
                <span className="inline-flex items-start gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  <History className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span>Lançamento retroativo: registrado em <strong className="whitespace-nowrap">{fmtData(data)}</strong> e contabilizado nessa competência.</span>
                </span>
              ) : (
                <span className="text-muted-foreground">Dispensação de hoje ({fmtData(data)}). Altere a data para corrigir lançamentos de dias anteriores.</span>
              )}
            </div>
          </div>

          {/* Item */}
          <div className="space-y-1">
            <Label className="text-sm">Item *</Label>
            <Combobox
              options={estoqueItens.map(i => ({
                value: i.id,
                label: i.nome,
                subtitle: `${i.tipo === 'medicamento' ? 'Medicamento' : 'Material'} · Estoque: ${i.estoque_atual} ${i.unidade}`,
              }))}
              value={form.item_id}
              onValueChange={(v) => setForm(f => ({ ...f, item_id: v }))}
              placeholder="Buscar medicamento ou material pelo nome..."
              searchPlaceholder="Digite o nome..."
              emptyText="Nenhum item com estoque encontrado"
              data-testid="select-np-item"
            />
            {itemSel && (
              <div className="flex flex-wrap items-center gap-2 text-xs pt-1" data-testid="np-item-detalhe">
                {itemSel.tipo === 'medicamento' ? <Pill className="h-3.5 w-3.5 text-purple-600" /> : <Package className="h-3.5 w-3.5 text-blue-600" />}
                <span className="font-medium">{itemSel.nome}</span>
                <Badge variant="outline" className="text-[10px]">{itemSel.tipo === 'medicamento' ? 'Medicamento' : 'Material'}</Badge>
                <span className="text-muted-foreground">Estoque disponível: <strong>{itemSel.estoque_atual} {itemSel.unidade}</strong></span>
                {itemSel.concentracao && <span className="text-muted-foreground">· {itemSel.concentracao}</span>}
              </div>
            )}
          </div>

          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-5 sm:col-span-3 space-y-1">
              <Label className="text-sm">Quantidade * {itemSel ? <span className="text-muted-foreground font-normal">({itemSel.unidade})</span> : null}</Label>
              <Input
                type="number" step="0.5" min="0.5"
                value={form.quantidade}
                onChange={(e) => setForm(f => ({ ...f, quantidade: e.target.value }))}
                className={qtdInvalida && form.item_id ? 'border-red-400' : ''}
                data-testid="input-np-quantidade"
              />
            </div>
            <div className="col-span-7 sm:col-span-9 flex items-center gap-3">
              <Button
                variant="outline" className="gap-1"
                onClick={handleAddToCart}
                disabled={!form.item_id || qtdInvalida}
                data-testid="btn-add-np"
              >
                <Plus className="h-4 w-4" /> Adicionar ao carrinho
              </Button>
              {itemSel && qtdInvalida && <span className="text-xs text-red-600" data-testid="np-qtd-erro">Quantidade inválida ou acima do estoque</span>}
            </div>
          </div>

          {cart.length > 0 && (
            <div className="border rounded-lg overflow-hidden" data-testid="np-carrinho">
              <div className="bg-muted/50 px-3 py-2 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                <span className="text-sm font-medium">Carrinho ({cart.length} {cart.length === 1 ? 'item' : 'itens'}) · {fmtData(data)}</span>
              </div>
              <div className="divide-y max-h-40 overflow-y-auto">
                {cart.map((entry) => (
                  <div key={entry._id} className="flex items-center justify-between px-3 py-2" data-testid={`np-carrinho-item-${entry.item_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className="text-xs shrink-0">
                        {entry.item_tipo === 'medicamento' ? 'MED' : 'MAT'}
                      </Badge>
                      <span className="text-sm truncate">{entry.item_nome}</span>
                      <span className="text-xs text-muted-foreground shrink-0">× {entry.quantidade} {entry.item_unidade}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveFromCart(entry._id)} className="h-7 w-7 p-0 text-red-500" data-testid={`btn-remover-np-${entry.item_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="font-semibold text-red-700">Justificativa *</Label>
            <Textarea
              value={form.justificativa}
              onChange={(e) => setForm(f => ({ ...f, justificativa: e.target.value }))}
              placeholder="Descreva o motivo da dispensação fora da prescrição (mínimo 5 caracteres)..."
              rows={3}
              className="resize-none"
              data-testid="textarea-np-justificativa"
            />
            {form.justificativa.length > 0 && form.justificativa.trim().length < 5 && (
              <p className="text-xs text-red-500">Mínimo 5 caracteres</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="btn-cancelar-np">Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || cart.length === 0 || form.justificativa.trim().length < 5 || dataInvalida}
            className="bg-red-600 hover:bg-red-700"
            data-testid="btn-confirmar-np"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Confirmar dispensação ({cart.length} {cart.length === 1 ? 'item' : 'itens'}){retroativa ? ` em ${fmtData(data)}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DispensacaoNaoPrescritoModal;
